import type { ILoginSuccessView, ILoginUser } from '@shared/services';
import { publicApi } from '../../client';

/**
 * `ILoginUser` carries the optional `accountsProof` (inherited from `IAuthCredentials`): sent on the
 * final `login` when the flow cleared MFA at `check-credentials`, so the backend does not raise a
 * second challenge. Omitted when no MFA was required, preserving the legacy request shape.
 */
export type LoginRequestParams = ILoginUser;

/**
 * Password-change challenge returned by `login` for a valid credential whose password policy is
 * below the current version. The REST view is camelCase and discriminated on `outcome`. Defined
 * locally because `@shared/services` does not yet export a REST challenge view.
 */
export interface ILoginChallengeView {
  outcome: 'password_change_required';
  changeToken: string;
  policyVersion: number;
}

/**
 * MFA challenge returned by `login` for a correct credential (after any password-change gate) that
 * belongs to an MFA-required account. Like the password-change view it is camelCase and
 * discriminated on `outcome`, and carries a short-lived `mfaPending` bearer token (no session) that
 * authorises the `/auth/mfa/*` ceremony. `mfaMethods` lists the methods to set up
 * (`mfa_enrollment_required`) or the configured factors to satisfy (`mfa_required`). Defined locally
 * because `@shared/services` does not yet export a REST challenge view.
 */
export interface IMfaChallengeView {
  outcome: 'mfa_enrollment_required' | 'mfa_required';
  mfaPending: string;
  mfaMethods: string[];
  reason: 'mfa';
}

export type LoginResponse = ILoginSuccessView | ILoginChallengeView | IMfaChallengeView;

export const isPasswordChangeChallenge = (response: LoginResponse): response is ILoginChallengeView =>
  'outcome' in response && response.outcome === 'password_change_required';

/** An MFA challenge for an account that must VERIFY an already-configured factor (handled inline). */
export const isMfaVerifyChallenge = (response: LoginResponse): response is IMfaChallengeView =>
  'outcome' in response && response.outcome === 'mfa_required';

/** An MFA challenge for an account that must SET UP a factor first (an inline login-wizard step). */
export const isMfaEnrollmentChallenge = (response: LoginResponse): response is IMfaChallengeView =>
  'outcome' in response && response.outcome === 'mfa_enrollment_required';

export async function loginRequest(params: LoginRequestParams): Promise<LoginResponse> {
  // Opt into the password-change challenge via header so the backend may answer an outdated
  // credential with a `password_change_required` outcome instead of tokens.
  const { data } = await publicApi.post('/api/v1/auth/login', params, {
    headers: { 'x-auth-supports-challenges': 'true' },
  });

  return data;
}
